import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import CafeCard from "@/components/CafeCard";
import CafeMap from "@/components/CafeMap";
import { ArrowLeft, MapPin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/cities/${slug}`)
      .then(({ data }) => setCity(data))
      .catch((e) => setError(e.response?.data?.detail || "City not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  useDocumentMeta({
    title: city ? `Best cafés in ${city.name}` : undefined,
    description: city
      ? (city.count > 0
        ? `${city.count} cafés and bean shops in ${city.name}, ${city.country}. Curated and reviewed by the BrewFinder community.`
        : `${city.name} doesn't have a single listing yet — be the first to submit a café and start the local guide.`)
      : undefined,
  });

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="h-72 bg-card border border-border rounded-sm animate-pulse" />
    </div>;
  }
  if (error || !city) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-center" data-testid="city-not-found">
        <p className="font-serif text-3xl">{error || "City not found"}</p>
        <Link to="/directory" className="text-[#C05A46] mt-4 inline-block">← Browse all cafés</Link>
      </div>
    );
  }

  return (
    <div data-testid="city-page" className="pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
        <Link
          to="/directory"
          className="text-xs uppercase tracking-wider text-[#786B63] hover:text-[#C05A46] inline-flex items-center gap-1"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All cities
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">City guide</p>
        <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-[1.05]">
          {city.count > 0 ? <>Best cafés in <em className="font-normal italic">{city.name}</em>.</> : <><em className="font-normal italic">{city.name}</em> — waiting for its first café.</>}
        </h1>
        <p className="text-[#786B63] mt-3 max-w-2xl">
          {city.count > 0
            ? <>{city.count} {city.count === 1 ? "spot" : "spots"} reviewed by the BrewFinder community. From quiet pour-over bars to roasteries worth a detour.</>
            : <>We don't have any listings in {city.name} yet. Know a great café here? Submit the first one — be a founding contributor for this city.</>}
        </p>
      </section>

      {city.count === 0 ? (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 mt-12">
          <div className="border border-border bg-card rounded-sm p-10 text-center" data-testid="city-empty-state">
            <Plus className="h-7 w-7 text-[#C05A46] mx-auto" strokeWidth={1.5} />
            <p className="font-serif text-3xl mt-4">Be the first.</p>
            <p className="text-sm text-[#786B63] mt-2 max-w-md mx-auto leading-relaxed">
              Locals always know the best coffee. Submit your favorite spot in {city.name} and start the {city.name} guide.
            </p>
            <Button
              asChild
              className="mt-6 rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]"
              data-testid="city-empty-submit"
            >
              <Link to="/submit">Submit the first café</Link>
            </Button>
          </div>
        </section>
      ) : (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 grid md:grid-cols-[1fr_420px] gap-8">
        <div className="grid sm:grid-cols-2 gap-6">
          {city.cafes.map((c) => <CafeCard key={c.id} cafe={c} />)}
        </div>
        <div className="md:sticky md:top-20 self-start">
          <CafeMap cafes={city.cafes} height="h-[480px]" />
          <p className="mt-3 text-xs text-[#786B63] inline-flex items-center gap-1.5">
            <MapPin size={12} strokeWidth={1.5} /> {city.name}, {city.country}
          </p>
        </div>
      </section>
      )}
    </div>
  );
}
